import _ from "lodash";
import React from "react";

import { colors } from "@toddle-edu/ds-theme";
import {
  SectionOutlined,
  SetupOutlined,
  JournalOutlined,
  SettingsOutlined,
  PencilLineOutlined,
  DeleteOutlined,
  CalculatorOutlined,
  AddOutlined,
  PercentageOutlined,
  UpDownArrowOutlined,
} from "@toddle-edu/ds-icons";

import {
  checkSMSSendVisible,
  getAttendanceSettingsV2M2Enabled,
  getAttendanceTiersEnabled,
  getIsRnpConsumptionAllowed,
} from "AttendanceDashboard/modules/Utils";
import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";
import {
  checkIsAttendanceCodeEnhancementEnabled,
  getAttendanceOptionRnpRolesEnabled,
  getIsAttendanceCodesSortingEnabled,
} from "AttendanceAndTimetableSettings/modules/Utils";

import ACLStorage from "lib/aclService";
import { ATTENDANCE_CONSTANTS } from "AttendanceDashboard/constants/stringConstants";
import {
  SCHOOL_WIDE_SETTINGS_ACCESS_PERMISSION,
  ENABLE_MODULE_SETTINGS_IN_PROGRAMME_WIDE_CONFIGURATIONS,
  MODULE_SETTINGS_V2,
  COMBINE_CURRICULUM_HOME_PAGE_PERMISSION,
} from "Constants/permissionConstants";
import ACLStore from "lib/aclStore";
import { getIsNewAdminUIEnabled } from "Utils/ACLStoreUtils";

const styles = {
  fontStyles: {
    fontSize: "1.4rem",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeigth: "2.0rem",
  },
};

export const getSideNavigationOptions = ({ t, isSchoolPortalRoute }) => {
  const divider = {
    key: "DIVIDER",
    isDivider: true,
    isVisible: true,
  };

  const navigation = [
    {
      children: isSchoolPortalRoute ? ["SETTINGS"] : ["SETUP", "SETTINGS"],
      key: "root",
      isVisible: true,
    },
    {
      children: getAttendanceSettingsV2M2Enabled()
        ? ["SETUP_GUIDE", "DIVIDER"]
        : [],
      key: "SETUP",
      parent: "root",
      tier: 2,
      isVisible: !isSchoolPortalRoute,
    },
    {
      children: [],
      key: "SETUP_GUIDE",
      parent: "SETUP",
      tier: 1,
      title: t("attendance:setup_assistant"),
      prefix: <SetupOutlined />,
      isVisible: !isSchoolPortalRoute,
    },
    divider,
    {
      children: [
        ...(!isSchoolPortalRoute
          ? getAttendanceTiersEnabled()
            ? ["ATTENDANCE_CATEGORIES_V2", "ATTENDANCE_TIERS"]
            : ["ATTENDANCE_CATEGORIES_V2"]
          : []),
        "ATTENDANCE_RECORDING",
        ...(getAttendanceSettingsV2M2Enabled()
          ? ["ATTENDANCE_CALCULATION"]
          : []),
        "PERMISSIONS",
        "DIVIDER",
      ],
      key: "SETTINGS",
      parent: "root",
      tier: 2,
      isVisible: true,
    },
    {
      children: [],
      key: "ATTENDANCE_CATEGORIES_V2",
      parent: "SETTINGS",
      tier: 1,
      title: t("attendance:attendance_categories_and_codes"),
      prefix: <SectionOutlined />,
      isVisible: !isSchoolPortalRoute,
    },
    {
      children: [],
      key: "ATTENDANCE_RECORDING",
      parent: "SETTINGS",
      tier: 1,
      title: t("attendance:attendance_recording"),
      prefix: <JournalOutlined />,
      isVisible: true,
    },
    {
      children: [],
      key: "ATTENDANCE_TIERS",
      parent: "SETTINGS",
      tier: 1,
      title: "Tiers",
      prefix: <PercentageOutlined />,
      isVisible: !isSchoolPortalRoute && getAttendanceTiersEnabled(),
    },
    ...(getAttendanceSettingsV2M2Enabled()
      ? [
          {
            children: [],
            key: "ATTENDANCE_CALCULATION",
            parent: "SETTINGS",
            tier: 1,
            title: t("attendance:attendance_calculation"),
            prefix: <CalculatorOutlined />,
            isVisible: true,
          },
        ]
      : []),
    {
      children: [],
      key: "PERMISSIONS",
      parent: "SETTINGS",
      tier: 1,
      title: t("common:permission_plural"),
      prefix: <SettingsOutlined />,
      isVisible: true,
    },
    divider,
  ];

  return _.filter(navigation, { isVisible: true });
};

export const getCurriculumTabOptions = ({
  includeMasterList = false,
  curriculumPrograms,
  curriculumProgramModuleSettingsMap,
}) => {
  const masterListOption = {
    label: "Master list",
    value: "MASTER_LIST",
  };
  const curriculumOptions = _.map(curriculumPrograms, ({ id, acronym }) => {
    return {
      label: acronym,
      value: id,
    };
  });

  const filteredCurriculumOptions = _.filter(curriculumOptions, ({ value }) => {
    return _.get(curriculumProgramModuleSettingsMap, value, false);
  });

  return [
    ...(includeMasterList ? [masterListOption] : []),
    ...filteredCurriculumOptions,
  ];
};

export const getAttendanceCodesTableHeader = ({ t }) => {
  const columns = [
    {
      key: "ATTENDANCE_CODES",
      value: t("attendance:attendance_code_plural"),
      isSticky: true,
      isEnabled: true,
      style: {
        width: "248px",
        ...styles.fontStyles,
      },
    },
    ...(checkIsAttendanceCodeEnhancementEnabled()
      ? [
          {
            key: "IS_EXCUSED",
            value: t("attendance:is_excused"),
            alignment: "center",
            isEnabled: true,
            style: {
              minWidth: "128px",
              ...styles.fontStyles,
            },
          },
        ]
      : [
          {
            key: "PRESENT_IN_CAMPUS",
            value: t("attendance:present_in_campus"),
            alignment: "center",
            isEnabled: true,
            style: {
              minWidth: "128px",
              ...styles.fontStyles,
            },
          },
        ]),
    {
      key: "PROGRAMS_APPLICABLE",
      value: t("attendance:programmes_applicable"),
      alignment: "center",
      isEnabled: true,
      style: {
        width: "212px",
        ...styles.fontStyles,
      },
    },
    ...(checkIsAttendanceCodeEnhancementEnabled()
      ? [
          {
            key: "TYPE",
            value: t("common:type"),
            alignment: "center",
            isEnabled: true,
            style: {
              minWidth: "132px",
              ...styles.fontStyles,
            },
          },
        ]
      : []),
    ...(getAttendanceOptionRnpRolesEnabled()
      ? [
          {
            key: "ALLOWED_ROLES",
            value: t("attendance:allowed_roles"),
            alignment: "center",
            isEnabled: true,
            style: {
              minWidth: "132px",
              ...styles.fontStyles,
            },
          },
        ]
      : []),
    {
      key: "PRESENCE_VALUE",
      value: t("common:value"),
      alignment: "center",
      isEnabled: true,
      style: {
        minWidth: "132px",
        ...styles.fontStyles,
      },
    },
    {
      key: "EMAILS_TO_FAMILY",
      value: `${t("common:email")} ${t("attendance:to_family")}`,
      alignment: "center",
      isEnabled: true,
      style: {
        minWidth: "116px",
        ...styles.fontStyles,
      },
    },
    {
      key: "SMS_TO_FAMILY",
      value: `${t("attendance:sms")} ${t("attendance:to_family")}`,
      alignment: "center",
      isEnabled: checkSMSSendVisible(),
      style: {
        minWidth: "116px",
        ...styles.fontStyles,
      },
    },

    ...(getIsAttendanceCodesSortingEnabled()
      ? [
          {
            key: "SORT_ORDER",
            value: t("common:sort_order"),
            alignment: "center",
            isEnabled: true,
            style: {
              minWidth: "120px",
              ...styles.fontStyles,
            },
          },
        ]
      : []),

    {
      key: "ACTIONS",
      isEnabled: true,
      alignment: "right",
      style: {
        width: "64px",
        paddingRight: "8px",
      },
    },
  ];

  return _.filter(columns, ({ isEnabled }) => isEnabled);
};

export const getActionDropDownOptions = ({
  t,
  entity,
  isEntityUsed,
  isFromCategory = false,
  isDefault,
}) => {
  return [
    ...(isFromCategory
      ? [
          {
            key: ATTENDANCE_CONSTANTS.CREATE,
            label: t("common:create_a_with_label", { label: t("common:code") }),
            icon: <AddOutlined />,
          },
        ]
      : []),
    {
      key: ATTENDANCE_CONSTANTS.EDIT,
      label: t("common:edit_with_label", { label: entity }),
      icon: <PencilLineOutlined />,
    },
    ...(getIsAttendanceCodesSortingEnabled() && !isFromCategory
      ? [
          {
            key: ATTENDANCE_CONSTANTS.CHANGE_ORDER,
            label: t("common:change_order"),
            icon: <UpDownArrowOutlined />,
          },
        ]
      : []),
    ...(!isDefault
      ? [
          {
            key: ATTENDANCE_CONSTANTS.DELETE,
            label: t("common:delete_with_label", { label: entity }),
            isDestructive: true,
            icon: <DeleteOutlined />,
            disabled: isEntityUsed,
            disabledTooltip: isFromCategory
              ? t("attendance:delete_attendance_category_tooltip")
              : t("attendance:delete_code_tooltip"),
            tooltipPlacement: "left",
          },
        ]
      : []),
  ];
};

export const getSegmentedProgressByStep = ({ step }) => {
  const step1Color = _.includes(["STEP_1", "STEP_2"], step)
    ? colors.surfaceDark
    : colors.neutral600;
  const step2Color = _.includes(["STEP_2"], step)
    ? colors.surfaceDark
    : colors.neutral600;

  return [
    {
      id: "STEP_1",
      color: step1Color,
      progress: 49.5,
    },
    {
      id: "GAP_1",
      color: "#ffffff",
      progress: 1,
    },
    {
      id: "STEP_2",
      color: step2Color,
      progress: 49.5,
    },
  ];
};

const getOrganizationMultiCurriculumAttendanceCodesVariables = ({
  organizationId,
  curriculumProgramIds,
  academicYearId,
}) => {
  return {
    id: organizationId,
    organizationAttendanceFilters: {
      academicYearIds: [academicYearId],
    },
  };
};

export const getOrganizationMultiCurriculumAttendanceCodesVariablesMemoized = _.memoize(
  params => getOrganizationMultiCurriculumAttendanceCodesVariables(params),
  JSON.stringify
);

const getOrganizationAttendanceCategoriesVariables = ({
  organizationId,
  academicYearId,
}) => {
  return {
    id: organizationId,
    organizationAttendanceFilters: {
      academicYearIds: [academicYearId],
    },
  };
};

export const getOrganizationAttendanceCategoriesVariablesMemoized = _.memoize(
  params => getOrganizationAttendanceCategoriesVariables(params),
  JSON.stringify
);

export const getAttendanceCodeToggleSettingsHeader = ({ t }) => {
  const columns = [
    {
      key: "TOGGLE_SWITCH",
      value: "",
      isEnabled: true,
      style: {
        fontStyle: "normal",
        fontWeight: 600,
        fontSize: "14px",
        width: "48px",
      },
    },
    {
      key: "PROGRAM",
      value: t("common:programme"),
      isEnabled: true,
      style: {
        fontStyle: "normal",
        fontWeight: 600,
        fontSize: "14px",
        minWidth: "83px",
      },
    },
    {
      key: "PRESENCE_VALUE",
      value: t("common:value"),
      isEnabled: getAttendanceSettingsV2M2Enabled(),
      alignment: "center",
      style: {
        fontStyle: "normal",
        fontWeight: 600,
        fontSize: "14px",
        width: "116px",
      },
    },

    {
      key: "EMAILS_TO_FAMILY",
      value: `${t("common:email")} ${t("attendance:to_family")}`,
      isEnabled: true,
      alignment: "center",
      style: {
        fontStyle: "normal",
        fontWeight: 600,
        fontSize: "14px",
        minWidth: "116px",
      },
    },
    {
      key: "SMS_TO_FAMILY",
      value: `${t("attendance:sms")} ${t("attendance:to_family")}`,
      isEnabled: checkSMSSendVisible(),
      alignment: "center",
      style: {
        fontStyle: "normal",
        fontWeight: 600,
        fontSize: "14px",
        minWidth: "116px",
      },
    },
  ];

  return _.filter(columns, ({ isEnabled }) => isEnabled);
};

export const getInitialToggleStatus = ({
  schoolProgramSettings,
  curriculumProgramSettings,
  codeCurriculumPrograms,
}) => {
  const initialToggleStatus = {};

  if (curriculumProgramSettings) {
    _.map(
      curriculumProgramSettings,
      ({ curriculumProgramId, sendEmail, sendSMS, status, value }) => {
        initialToggleStatus[curriculumProgramId] = {
          sendEmail,
          sendSMS,
          status,
          value: _.toNumber(value)?.toFixed(2),
        };
      }
    );
    return initialToggleStatus;
  }

  if (schoolProgramSettings) {
    _.map(schoolProgramSettings, ({ curriculumProgram, settings }) => {
      initialToggleStatus[_.get(curriculumProgram, "id")] = {
        ...settings,
        value: _.toNumber(_.get(settings, "value")).toFixed(2),
      };
    });
  } else {
    _.map(codeCurriculumPrograms, ({ id }) => {
      initialToggleStatus[id] = {
        status: "DISABLED",
        sendSMS: false,
        sendEmail: false,
        value: _.toNumber(0)?.toFixed(2),
      };
    });
  }

  return initialToggleStatus;
};

export const percentageFormatOptions = ({ t }) => {
  return [
    {
      id: "DECIMAL",
      title: t("attendance:show_decimal_values"),
      options: [
        {
          id: "0",
          label: t("common:no_decimal_values"),
        },
        {
          id: "1",
          label: t("common:decimal_value_precision", {
            count: t("common:one_number"),
          }),
        },
        {
          id: "2",
          label: t("common:decimal_value_plural_precision", {
            count: t("common:two_number"),
          }),
        },
      ],
    },
    {
      id: "CALCULATION",
      title: t("attendance:how_should_scores_be_calculated"),
      options: [
        {
          id: "ROUND",
          label: t("attendance:round_scores", {
            example: 92.886,
            roundedExample: 92.89,
          }),
        },
        {
          id: "TRUNCATE",
          label: t("attendance:truncate_scores", {
            example: 92.882,
            truncatedExample: 92.88,
          }),
        },
      ],
    },
  ];
};

export const getAttendanceCalculationLabel = ({
  t,
  attendanceDecimal,
  attendanceCalculation,
}) => {
  let decimalLabel = "";
  let calculationLabel = "";

  switch (attendanceDecimal) {
    case "0":
      decimalLabel = t("common:no_decimal_values");
      break;
    case "1":
      decimalLabel = t("attendance:decimal_value_with_precision", { count: 1 });
      break;
    case "2":
      decimalLabel = t("attendance:decimal_value_plural_with_precision", {
        count: 2,
      });
  }

  switch (attendanceCalculation) {
    case "ROUND":
      calculationLabel = t("common:rounded_lowercase");
      break;
    case "TRUNCATE":
      calculationLabel = t("common:truncated_lowercase");
      break;
  }

  return `${decimalLabel}, ${calculationLabel}`;
};

export const attendanceTypeOptions = ({ t }) => {
  return [
    {
      id: "DAILY",
      label: t("attendance:daily_attendance"),
    },
    {
      id: "PERIOD",
      label: t("attendance:period_attendance"),
    },
  ];
};

export const getAttendanceSettingsSetupGuideTemplate = ({ t }) => {
  return [
    {
      id: "ATTENDANCE_CATEGORIES_V2",
      title: t("attendance:attendance_categories_and_codes"),
      subtitle: t(
        "attendance:attendance_categories_and_codes_setup_description"
      ),
      icon: <SectionOutlined variant="critical" className="" />,
      iconBackgroundColor: "var(--decorative-background-pink)",
      navigateToOtherRoute: false,
      route: "categoriesV2",
    },
    {
      id: "ATTENDANCE_RECORDING",
      title: t("attendance:attendance_recording"),
      subtitle: t("attendance:attendance_recording_setup_description"),
      icon: <JournalOutlined variant="critical" />,
      iconBackgroundColor: "var(--decorative-background-violet)",
      navigateToOtherRoute: false,
      route: "recording",
    },
    {
      id: "ATTENDANCE_CALCULATION",
      title: t("attendance:attendance_calculation"),
      subtitle: t("attendance:attendance_calculation_setup_description"),
      icon: <SectionOutlined variant="critical" />,
      iconBackgroundColor: "var(--decorative-background-teal)",
      navigateToOtherRoute: false,
      route: "calculation",
    },
    {
      id: "MODULE_SETTINGS",
      title: t("configuration:module_settings"),
      subtitle: t("attendance:module_settings_setup_description"),
      icon: <SectionOutlined variant="critical" />,
      iconBackgroundColor: "var(--decorative-background-yellow)",
      navigateToOtherRoute: true,
      showUserCurriculumProgramsModal: true,
    },
    {
      id: "PERMISSIONS",
      title: t("common:permission_plural"),
      subtitle: t("attendance:permissions_setup_description"),
      icon: <SectionOutlined variant="critical" />,
      iconBackgroundColor: "var(--decorative-background-blue)",
      navigateToOtherRoute: false,
      route: "excusal-settings",
    },
    {
      id: "TIMETABLE",
      title: t("common:timetable"),
      subtitle: t("attendance:timetable_setup_description"),
      icon: <SectionOutlined variant="critical" />,
      iconBackgroundColor: "var(--decorative-background-pink)",
      navigateToOtherRoute: true,
      showUserCurriculumProgramsModal: true,
    },
  ];
};

export const getAttendanceSettingsAdditionalSetupTemplate = ({ t }) => {
  return [
    {
      id: "ASSIGN_HOMEROOM_ADVISORS",
      title: t("attendance:assign_homeroom_advisors"),
      subtitle: t("attendance:assign_homeroom_advisors_setup_description"),
      icon: <SectionOutlined variant="critical" />,
      iconBackgroundColor: "var(--decorative-background-subtle-teal)",
      navigateToOtherRoute: false,
      showAssignHomeroomAdvisorsModal: true,
    },
  ];
};

export const getNavigationRouteForAttendanceModule = ({
  id,
  curriculumProgramIdToNavigate,
  courseId,
  learningCourseId,
}) => {
  const isNewAdminNavigationEnabled = getIsNewAdminUIEnabled();
  const isCombinedCurriculumHomepageEnabled = ACLStore.can(
    COMBINE_CURRICULUM_HOME_PAGE_PERMISSION
  );

  switch (id) {
    case "GRADING_PERIODS": {
      if (isCombinedCurriculumHomepageEnabled || isNewAdminNavigationEnabled) {
        return {
          route: isCombinedCurriculumHomepageEnabled
            ? `platform/accountSettings/grading-periods/${curriculumProgramIdToNavigate}`
            : `accountSettings/grading-periods/${curriculumProgramIdToNavigate}`,
          type: "push",
          replacePath: isCombinedCurriculumHomepageEnabled
            ? "platform"
            : "accountSettings",
        };
      }

      return {
        route: "administrator/schoolSetup/academic-year-terms",
        type: "push",
        replacePath: "administrator",
      };
    }
    case "SCHOOL_CALENDAR": {
      if (isCombinedCurriculumHomepageEnabled) {
        return {
          route: `platform/my-home/courses/my-calendar`,
          type: "push",
          replacePath: "platform",
        };
      }

      return curriculumProgramIdToNavigate
        ? {
            route: `platform/${curriculumProgramIdToNavigate}/courses/my-calendar`,
            type: "push",
            replacePath: "platform",
          }
        : null;
    }
    case "MODULE_SETTINGS": {
      if (isCombinedCurriculumHomepageEnabled) {
        return {
          route: `platform/school/${curriculumProgramIdToNavigate}/global-module-settings`,
          type: "push",
          replacePath: "platform",
        };
      }

      const isModuleSettingsEnabledInSchoolWideConfigurations = ACLStorage.getModuleSettingsValueByContext(
        SCHOOL_WIDE_SETTINGS_ACCESS_PERMISSION,
        []
      );

      /**
       * If org has module settings enabled at programme wide configurations
       * and if the user is not allowed to access module settings at school wide configurations
       * then navigate to programme specific module settings inside programme wide configurations
       */

      let oldRoute =
        ACLStore.can(ENABLE_MODULE_SETTINGS_IN_PROGRAMME_WIDE_CONFIGURATIONS) &&
        !isModuleSettingsEnabledInSchoolWideConfigurations
          ? `administrator/${curriculumProgramIdToNavigate}/settings/module-settings`
          : `administrator/school-settings/module-settings/${curriculumProgramIdToNavigate}`;

      let newRoute = `accountSettings/module-settings/${curriculumProgramIdToNavigate}`;

      // Redirect to new module settings if enabled for the organization
      if (ACLStore.can(MODULE_SETTINGS_V2)) {
        oldRoute = `administrator/${curriculumProgramIdToNavigate}/settings/module-settings-v2`;
        newRoute = `platform/school/${curriculumProgramIdToNavigate}/module-settings-v2/`;
      }

      return curriculumProgramIdToNavigate
        ? {
            route: isNewAdminNavigationEnabled ? newRoute : oldRoute,
            type: "push",
            replacePath: isNewAdminNavigationEnabled
              ? "accountSettings"
              : "administrator",
          }
        : null;
    }
    case "TIMETABLE": {
      if (isCombinedCurriculumHomepageEnabled) {
        return {
          route: `platform/school/${curriculumProgramIdToNavigate}/timetable/checklist`,
          type: "push",
          replacePath: "platform",
        };
      }

      const oldRoute = `administrator/${curriculumProgramIdToNavigate}/timetable`;
      const newRoute = `/platform/accountSettings/${curriculumProgramIdToNavigate}/timetable/checklist`;

      return {
        route: isNewAdminNavigationEnabled ? newRoute : oldRoute,
        type: "push",
        replacePath: "administrator",
      };
    }
    case "COURSE_ATTENDANCE": {
      return {
        route: `platform/${curriculumProgramIdToNavigate}/courses/${courseId}/attendance`,
        type: "push",
        replacePath: "platform",
      };
    }
    case "COURSE": {
      const isLearningCoursesEnabled =
        getIsLearningCoursesEnabled({
          curriculumProgramId: curriculumProgramIdToNavigate,
        }) && !!learningCourseId;

      const baseSegment = isCombinedCurriculumHomepageEnabled
        ? "my-home"
        : curriculumProgramIdToNavigate;

      return {
        route: isLearningCoursesEnabled
          ? `platform/${baseSegment}/learning-course/${learningCourseId}/class/${courseId}`
          : `platform/${baseSegment}/courses/${courseId}`,
        type: "push",
        replacePath: "platform",
      };
    }
  }
};

export const getModifyExcusalSettingsLabel = ({ value, t }) => {
  switch (value) {
    case "NONE":
      return t("common:not_allowed");
    case "ANY_DATES":
      return t("common:any_dates");
    case "CURRENT_AND_FUTURE_DATES":
      return t("attendance:future_dates_only_including_today");
  }
};

const getConfigurationTemplate = ({ curriculumProgramId, t }) => {
  const isRnpConsumptionAllowed = getIsRnpConsumptionAllowed();
  return [
    {
      uid: "ATTENDANCE",
      title: t("attendance:attendance"),
      conditionToCheck: {
        CURRICULUM_PROGRAM: curriculumProgramId,
      },
      childConfigurations: [
        ...(!isRnpConsumptionAllowed
          ? [
              {
                title: t(
                  "configuration:allow_teachers_to_edit_attendance_for_days_in_the_past"
                ),
                uid: "ALLOW_TEACHERS_TO_EDIT_ATTENDANCE_FOR_DAYS_IN_THE_PAST",
                uiType: "TOGGLE",
                perm: "EnableEditAttendanceInPastByTeacher",
                conditionToCheck: {
                  CURRICULUM_PROGRAM: curriculumProgramId,
                },
                level: "L1",
              },
              {
                title: t(
                  "configuration:allow_teachers_to_edit_attendance_of_students_in_progress_reports"
                ),
                uid:
                  "ALLOW_TEACHERS_TO_EDIT_ATTENDANCE_OF_STUDENTS_IN_PROGRESS_REPORTS",
                uiType: "TOGGLE",
                perm: "EnableEditAttendanceInProgressReportsByTeacher",
                conditionToCheck: {
                  CURRICULUM_PROGRAM: curriculumProgramId,
                },
                level: "L1",
              },
            ]
          : []),
        {
          title: t(
            "configuration:allow_family_members_to_see_attendance_notes"
          ),
          uid: "ALLOW_FAMILY_MEMBERS_TO_SEE_ATTENDANCE_NOTES",
          uiType: "TOGGLE",
          perm: "Common:ShowAttendanceNotesToParents",
          conditionToCheck: {
            CURRICULUM_PROGRAM: curriculumProgramId,
          },
          level: "L1",
        },
      ],
    },
    {
      uid: "EXCUSAL",
      title: t("attendance:excusal"),
      conditionToCheck: {
        CURRICULUM_PROGRAM: curriculumProgramId,
      },
      childConfigurations: [
        {
          title: t("attendance:allow_family_members_to_request_excusals"),
          uid: "ALLOW_FAMILY_MEMBERS_TO_REQUEST_EXCUSALS",
          uiType: "TOGGLE",
          perm: "EnableFamilyToRequestExcusal",
          conditionToCheck: {
            CURRICULUM_PROGRAM: curriculumProgramId,
          },
          level: "L1",
        },
        {
          title: t("attendance:allow_family_members_to_modify_excusals"),
          uid: "ALLOW_FAMILY_MEMBERS_TO_MODIFY_EXCUSALS",
          uiType: "ICON_BUTTON",
          modalType: "MODIFY_EXCUSALS",
          perm: "Setting:AllowExcusalToUpdateByDate",
          conditionToCheck: {
            CURRICULUM_PROGRAM: curriculumProgramId,
          },
          level: "L1",
        },
        {
          title: t("attendance:allow_homeroom_advisors_to_raise_excusals"),
          uid: "ALLOW_HOMEROOM_ADVISORS_TO_RAISE_EXCUSALS",
          uiType: "TOGGLE",
          perm: "Setting:AllowHomeroomStaffToRaiseExcusal",
          conditionToCheck: {
            CURRICULUM_PROGRAM: curriculumProgramId,
          },
          level: "L1",
        },
      ],
    },
  ];
};

// Checking if condition matches the fetched configuration's entityGroups
const isEntityGroupMatching = ({ condition, entityGroups }) => {
  return _.every(entityGroups, group =>
    _.some(
      _.keys(condition),
      key =>
        _.get(group, "entityType") === key &&
        _.get(group, "entityId") === condition[key]
    )
  );
};

// Finding matching configuration value by perm and condition
const findConfigValue = ({ perm, condition, configurations }) => {
  const configResponse = _.find(
    configurations,
    configuration =>
      _.some(
        configuration.configurations,
        config => _.get(config, "configuration") === perm
      ) &&
      isEntityGroupMatching({
        condition,
        entityGroups: _.get(configuration, "entityGroups"),
      })
  );

  if (configResponse) {
    const matchedConfig = _.find(
      configResponse.configurations,
      config => _.get(config, "configuration") === perm
    );

    return matchedConfig
      ? {
          value: _.get(matchedConfig, "value"),
          id: _.get(matchedConfig, "id"),
          aclControlParameterType: _.get(
            matchedConfig,
            "aclControlParameterType"
          ),
        }
      : null;
  }
  return null;
};

export const mapConfigurationToValue = ({
  curriculumProgramId,
  configurations,
  t,
}) => {
  const configurationTemplate = getConfigurationTemplate({
    curriculumProgramId,
    t,
  });

  return _.map(configurationTemplate, templateItem => {
    const { perm, conditionToCheck } = templateItem;

    const parentConfig = findConfigValue({
      perm,
      condition: conditionToCheck,
      configurations,
    });
    const {
      value: parentConfigValue,
      id: parentConfigId,
      aclControlParameterType: parentAclControlParameterType,
    } = parentConfig || {};
    templateItem.id = parentConfigId;
    templateItem.value = parentConfigValue;
    templateItem.aclControlParameterType = parentAclControlParameterType;

    // Map value for each child configuration
    if (_.get(templateItem, "childConfigurations")) {
      templateItem.childConfigurations = _.map(
        _.get(templateItem, "childConfigurations"),
        childItem => {
          const { perm, conditionToCheck } = childItem;
          const childConfig = findConfigValue({
            perm,
            condition: conditionToCheck,
            configurations,
          });
          return {
            ...childItem,
            id: _.get(childConfig, "id"),
            value: _.get(childConfig, "value"),
            aclControlParameterType: _.get(
              childConfig,
              "aclControlParameterType"
            ),
          };
        }
      );
    }

    return templateItem;
  });
};

export const getUpdatedMapConfigurationToValue = ({
  configurations,
  uid,
  newValue,
}) => {
  const parentConfig = _.find(configurations, config => config.uid === uid);

  if (parentConfig) {
    // If we find the config at L0, directly update the value
    _.set(parentConfig, "value", newValue);
  } else {
    // If not found, check all childConfigurations at L1
    const topLevelConfig = _.find(configurations, config =>
      _.find(
        _.get(config, "childConfigurations"),
        child => _.get(child, "uid") === uid
      )
    );

    if (topLevelConfig) {
      const childConfig = _.find(
        _.get(topLevelConfig, "childConfigurations"),
        child => _.get(child, "uid") === uid
      );
      _.set(childConfig, "value", newValue);
    }
  }

  return configurations;
};

export const getConfigurationUpdateInput = ({
  level,
  value: configValue,
  id,
  conditionToCheck,
  childConfigEntityGroups,
}) => {
  return level === "L0" && configValue
    ? [
        {
          id,
          valueType: "BOOLEAN",
          entityGroups: [
            _.map(_.keys(conditionToCheck), entityType => {
              const entityId = conditionToCheck[entityType];
              return {
                entityType,
                entityId,
              };
            }),
          ],
          value: JSON.stringify(false),
        },
        ..._.map(childConfigEntityGroups, childConfigEntityGroup => {
          return {
            id,
            valueType: "BOOLEAN",
            entityGroups: [
              _.map(_.keys(childConfigEntityGroup), entityType => {
                const entityId = childConfigEntityGroup[entityType];
                return {
                  entityType,
                  entityId,
                };
              }),
            ],
            value: JSON.stringify(false),
          };
        }),
      ]
    : [
        {
          id,
          valueType: "BOOLEAN",
          entityGroups: [
            _.map(_.keys(conditionToCheck), entityType => {
              const entityId = conditionToCheck[entityType];
              return {
                entityType,
                entityId,
              };
            }),
          ],
          value: JSON.stringify(!configValue),
        },
      ];
};

export const getIsAttendanceCategoryColorEnabled = () => {
  return (
    ACLStore.can("FeatureFlag:EnableAttendanceCategoryColor") ||
    ACLStore.can("FeatureFlag:Toddle2.0Timetable")
  );
};

export const isPermissionIsEnabledInAnyCurriculum = ({
  permission,
  curriculumProgramIds,
}) => {
  return _.some(curriculumProgramIds, curriculumProgramId => {
    return ACLStorage.getPermissionValueByContext(permission, [
      { entityType: "CURRICULUM_PROGRAM", entityId: curriculumProgramId },
    ]);
  });
};

export const checkIsAttendanceHomeroomClassesEnabled = () => {
  return ACLStore.can("FeatureFlag:AttendanceHomeroomClasses");
};

// Mapping between sidebar keys and route paths
export const SIDEBAR_KEY_TO_ROUTE_MAP = {
  SETUP_GUIDE: "setup-guide",
  ATTENDANCE_CATEGORIES_V2: "categoriesV2",
  ATTENDANCE_RECORDING: "recording",
  ATTENDANCE_TIERS: "tiers",
  ATTENDANCE_CALCULATION: "calculation",
  PERMISSIONS: "excusal-settings", // Both EXCUSALS and PERMISSIONS map to excusals route
};

// Reverse mapping for route to sidebar key
export const ROUTE_TO_SIDEBAR_KEY_MAP = {
  "setup-guide": "SETUP_GUIDE",
  categoriesV2: "ATTENDANCE_CATEGORIES_V2",
  recording: "ATTENDANCE_RECORDING",
  tiers: "ATTENDANCE_TIERS",
  calculation: "ATTENDANCE_CALCULATION",
  "excusal-settings": "PERMISSIONS",
};

export const getCurriculumProgramModuleSettingsMap = ({
  curriculumPrograms,
  permission,
}) => {
  return _.reduce(
    curriculumPrograms,
    (acc, { id }) => {
      acc[id] = ACLStorage.getModuleSettingsValueByContext(
        permission,
        [
          {
            entityType: "CURRICULUM_PROGRAM",
            entityId: _.toString(id),
          },
        ],
        { addOnContexts: [] }
      );
      return acc;
    },
    {}
  );
};
